import type { SupportAttachment } from '@travel-planner/shared/schemas';
import type { ClipboardEvent } from 'react';
import { useState } from 'react';
import { toastStore } from '@/shared/ui';
import { useUploadThing } from '@/shared/uploadthing';
import { filesFromClipboard } from '../lib/clipboard-files';
import { validateAttachmentFile } from '../lib/validate-attachment';

// Attachment state + upload for the ticket form (create and edit). One
// validate-then-upload path shared by the file picker and the clipboard paste,
// so both entry points enforce the same type/size rules and surface the same
// rejection toasts.
export const useTicketAttachments = (uploadsEnabled: boolean) => {
  const [attachments, setAttachments] = useState<SupportAttachment[]>([]);

  const { startUpload, isUploading } = useUploadThing('fileUploader', {
    onClientUploadComplete: (files) => {
      if (!files) return;
      setAttachments((prev) => [
        ...prev,
        ...files.map((file) => ({
          key: file.key,
          url: file.ufsUrl,
          name: file.name,
        })),
      ]);
    },
    onUploadError: (error) => {
      const notConfigured = /not configured|503|unavailable/i.test(
        error.message
      );
      toastStore.getState().show({
        message: notConfigured
          ? 'Attachments are unavailable — file storage is not configured.'
          : `Upload failed: ${error.message}`,
      });
    },
  });

  const uploadValidFiles = (files: File[]) => {
    const valid: File[] = [];
    for (const file of files) {
      const error = validateAttachmentFile(file);
      if (error) {
        toastStore.getState().show({ message: error });
        continue;
      }
      valid.push(file);
    }
    if (valid.length) startUpload(valid);
    return valid.length;
  };

  const removeAttachment = (key: string) =>
    setAttachments((prev) => prev.filter((a) => a.key !== key));

  // Clipboard paste → attachment. A screenshot on the clipboard only ever
  // reaches us as a paste event (the file picker cannot see it), so this is
  // the ONLY way to attach one without saving it to disk first. Bound at the
  // form level so it works wherever the caret is — including the Description
  // textarea, the common case. A text-only paste is left completely alone
  // (no preventDefault) so normal typing/pasting still works.
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!uploadsEnabled || isUploading) return;
    const files = filesFromClipboard(
      event.clipboardData,
      // Stamp is a local wall-clock marker used only to name the file.
      new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    );
    if (files.length === 0) return;
    // Files present — keep the image out of the textarea's text content.
    event.preventDefault();
    const uploaded = uploadValidFiles(files);
    if (uploaded > 0) {
      toastStore.getState().show({
        message:
          uploaded === 1
            ? 'Pasted image added as an attachment'
            : `${uploaded} pasted files added as attachments`,
      });
    }
  };

  return {
    attachments,
    setAttachments,
    isUploading,
    uploadValidFiles,
    removeAttachment,
    onPaste,
  };
};
